/**
 * Exact rational re-timing (W1-10a; DESIGN.md §5.2 "Edit policy", rpe-format F§3.2).
 *
 * BPM values are decimals (128.37 = 12837/100), so beat <-> seconds conversions are exact rationals: with the tempo
 * points (b_k, T_k, bpm_k) sorted by beat, T_0 = 0 and T_k = T_{k-1} + (b_k - b_{k-1}) * 60 / bpm_{k-1}:
 *   sec(b)  = T_k + (b - b_k) * 60 / bpm_k     k = last point with b_k <= b (0 if none: the first BPM extrapolates)
 *   beat(s) = b_k + (s - T_k) * bpm_k / 60     k = last point with T_k <= s (0 if none)
 * This is Phira's formula evaluated without rounding, so "keep time" keeps every record's seconds EXACTLY. Every
 * other value is counted once, in one of two classes:
 *  - rounded: its exact beat has a reduced denominator above the RPE u32 limit, so it is written as the nearest
 *    1/1920 beat (`RETIME_SNAP_DIV`); it is at most half a step (1/3840 beat) from its exact time;
 *  - nudged: it has a predecessor in an ordered list (the previous key of its track, a hold's start, an effect's
 *    start, the previous meter mark) and its exact or rounded beat would not come after that predecessor's new beat,
 *    so it is written exactly one 1/1920 step after the predecessor. Its distance from its exact time is therefore
 *    below one step plus the predecessor's own distance (two values closer than 1/3840 beat, the first rounded up).
 *
 * Keep-time edits (`solveKeepTime`): every BPM point except the edited one keeps its seconds; the edited point sits
 * at its (new) beat with its (new) BPM. The new list is built by sweeping the kept points in time order and placing
 * the edited point where its beat falls, so moving a point past others works too.
 */

import { Rat, type BigRational } from '../math/rational';
import type { BpmId, ChartDoc, DocIndexes, Key, KeyId, MeterMark, Note, TrackId } from '../model/types';
import { beatFromRatExact, beatToRat, Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { Patch, Tx } from './structureKit';

/** Grid that beats are snapped to when the exact rational cannot be written. */
export const RETIME_SNAP_DIV = 1920;

const SIXTY = Rat.of(60);

/** A BPM value as the exact decimal rational it was written as (128.37 -> 12837/100). */
export function bpmToRat(bpm: number): BigRational {
  return Rat.fromDecimal(String(bpm)) ?? Rat.fromF64(bpm);
}

export interface RatTempoPoint {
  readonly beat: BigRational;
  readonly sec: BigRational;
  /** Seconds per beat, 60 / bpm. */
  readonly spb: BigRational;
}

/** Exact tempo map (points sorted by beat; the first defines second 0). */
export interface RatTempo {
  readonly points: readonly RatTempoPoint[];
}

export interface TempoPointLike {
  readonly t: Beat;
  readonly bpm: number;
}

/** Builds the exact map from points sorted by beat (Phira: T_0 = 0 at the first point). */
export function ratTempo(points: readonly TempoPointLike[]): RatTempo {
  const out: RatTempoPoint[] = [];
  for (const p of points) {
    const beat = beatToRat(p.t);
    const spb = Rat.div(SIXTY, bpmToRat(p.bpm));
    const prev = out[out.length - 1];
    const sec = prev === undefined ? Rat.ZERO : Rat.add(prev.sec, Rat.mul(Rat.sub(beat, prev.beat), prev.spb));
    out.push({ beat, sec, spb });
  }
  if (out.length === 0) throw new RangeError('retime: the tempo map needs at least one point');
  return { points: out };
}

/** Exact map of a document's BPM table. */
export function docRatTempo(doc: Pick<ChartDoc, 'bpm'>, idx: Pick<DocIndexes, 'bpmSorted'>): RatTempo {
  const pts: TempoPointLike[] = [];
  for (const id of idx.bpmSorted()) {
    const p = doc.bpm.get(id);
    if (p !== undefined) pts.push(p);
  }
  return ratTempo(pts);
}

/** Last index k with key(points[k]) <= x, or 0. */
function lastAtOrBefore(points: readonly RatTempoPoint[], x: BigRational, by: 'beat' | 'sec'): number {
  let lo = 0;
  let hi = points.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const p = points[mid] as RatTempoPoint;
    if (Rat.cmp(p[by], x) <= 0) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

/** Exact seconds of a rational beat. */
export function ratSecAt(rt: RatTempo, b: BigRational): BigRational {
  const p = rt.points[lastAtOrBefore(rt.points, b, 'beat')] as RatTempoPoint;
  return Rat.add(p.sec, Rat.mul(Rat.sub(b, p.beat), p.spb));
}

/** Exact beat of rational seconds. */
export function ratBeatAt(rt: RatTempo, s: BigRational): BigRational {
  const p = rt.points[lastAtOrBefore(rt.points, s, 'sec')] as RatTempoPoint;
  return Rat.add(p.beat, Rat.div(Rat.sub(s, p.sec), p.spb));
}

/** A rational beat as a Beat: exact when it fits RPE's triple, else snapped to 1/1920. Null when out of range. */
export function ratToBeat(r: BigRational): { readonly beat: Beat; readonly snapped: boolean } | null {
  const exact = beatFromRatExact(r);
  if (exact !== null) return { beat: exact, snapped: false };
  const snapped = beatFromRatExact(Rat.roundTo(r, RETIME_SNAP_DIV));
  return snapped === null ? null : { beat: snapped, snapped: true };
}

/** One snap step after `b`. */
export function nudgeAfter(b: Beat): Beat {
  return Beats.step(b, RETIME_SNAP_DIV, 1);
}

// ---------------------------------------------------------------------------------------------------------------
// Keep-time tempo solving
// ---------------------------------------------------------------------------------------------------------------

export interface SolvedPoint {
  /** null = the edited point when it is new. */
  readonly id: BpmId | null;
  readonly t: Beat;
  readonly bpm: number;
}

export type SolveResult =
  | { readonly ok: true; readonly points: readonly SolvedPoint[]; readonly snapped: number }
  | { readonly ok: false; readonly reason: 'collide' | 'range' };

export interface OldPoint extends TempoPointLike {
  readonly id: BpmId;
}

/**
 * New BPM list for a keep-time edit. `old` is sorted by beat; `edited` is the point being added (id null) or changed
 * (it keeps its id and is taken out of the kept points); `removed` is a deleted point. Every other point keeps its
 * seconds. The beat-0 point stays at beat 0.
 */
export function solveKeepTime(
  old: readonly OldPoint[],
  edited: { readonly id: BpmId | null; readonly t: Beat; readonly bpm: number } | null,
  removed: BpmId | null,
): SolveResult {
  const oldRt = ratTempo(old);
  const kept = old
    .map((p, i) => ({ p, sec: (oldRt.points[i] as RatTempoPoint).sec }))
    .filter(({ p }) => p.id !== removed && (edited === null || p.id !== edited.id));
  const placed: Array<{
    readonly id: BpmId | null;
    readonly t: Beat;
    readonly bpm: number;
    readonly rp: RatTempoPoint;
  }> = [];
  let snapped = 0;
  const editedBeat = edited === null ? null : beatToRat(edited.t);
  let editedDone = edited === null;

  const place = (id: BpmId | null, t: Beat, bpm: number, beat: BigRational, sec: BigRational): void => {
    placed.push({ id, t, bpm, rp: { beat, sec, spb: Rat.div(SIXTY, bpmToRat(bpm)) } });
  };
  const placeEdited = (): void => {
    if (edited === null || editedBeat === null) return;
    const cur = placed[placed.length - 1];
    const sec =
      cur === undefined ? Rat.ZERO : Rat.add(cur.rp.sec, Rat.mul(Rat.sub(editedBeat, cur.rp.beat), cur.rp.spb));
    place(edited.id, edited.t, edited.bpm, editedBeat, sec);
    editedDone = true;
  };

  if (editedBeat !== null && Rat.sign(editedBeat) === 0) placeEdited();
  for (const { p, sec } of kept) {
    const cur = placed[placed.length - 1];
    if (cur === undefined) {
      // The first kept point is the beat-0 point (second 0).
      place(p.id, Beats.ZERO, p.bpm, Rat.ZERO, Rat.ZERO);
      continue;
    }
    let base = cur;
    let candidate = Rat.add(base.rp.beat, Rat.div(Rat.sub(sec, base.rp.sec), base.rp.spb));
    if (!editedDone && editedBeat !== null && Rat.cmp(editedBeat, candidate) <= 0) {
      if (Rat.cmp(editedBeat, candidate) === 0) return { ok: false, reason: 'collide' };
      placeEdited();
      base = placed[placed.length - 1] as (typeof placed)[number];
      candidate = Rat.add(base.rp.beat, Rat.div(Rat.sub(sec, base.rp.sec), base.rp.spb));
    }
    const b = ratToBeat(candidate);
    if (b === null) return { ok: false, reason: 'range' };
    let beat = candidate;
    let at = sec;
    if (b.snapped) {
      snapped++;
      beat = beatToRat(b.beat);
      at = Rat.add(base.rp.sec, Rat.mul(Rat.sub(beat, base.rp.beat), base.rp.spb));
    }
    if (Rat.cmp(beat, base.rp.beat) <= 0) return { ok: false, reason: 'collide' };
    place(p.id, b.beat, p.bpm, beat, at);
  }
  if (!editedDone) placeEdited();
  return { ok: true, points: placed.map(({ id, t, bpm }) => ({ id, t, bpm })), snapped };
}

// ---------------------------------------------------------------------------------------------------------------
// Re-timing the document
// ---------------------------------------------------------------------------------------------------------------

/** Maps beats of one tempo map onto another through exact seconds; beats before `stableBefore` are unchanged. */
export class BeatRetimer {
  private readonly from: RatTempo;
  private readonly to: RatTempo;
  private readonly stableBefore: Beat | null;
  /** Values rounded to the 1/1920 grid (each within half a step of its exact time). */
  snapped = 0;
  /** Values placed one 1/1920 step after their predecessor to keep their list strictly ordered. */
  nudged = 0;
  private outOfRange = false;

  constructor(from: RatTempo, to: RatTempo, stableBefore: Beat | null) {
    this.from = from;
    this.to = to;
    this.stableBefore = stableBefore;
  }

  get failed(): boolean {
    return this.outOfRange;
  }

  /** New beat of `b` and whether it had to be rounded (not counted). */
  private convert(b: Beat): { readonly beat: Beat; readonly snapped: boolean } {
    if (this.stableBefore !== null && Beats.lt(b, this.stableBefore)) return { beat: b, snapped: false };
    const r = ratToBeat(ratBeatAt(this.to, ratSecAt(this.from, beatToRat(b))));
    if (r === null) {
      this.outOfRange = true;
      return { beat: b, snapped: false };
    }
    return r;
  }

  /** New beat of `b` (exact, or rounded to 1/1920 and counted in `snapped`). */
  map(b: Beat): Beat {
    const r = this.convert(b);
    if (r.snapped) this.snapped++;
    return r.beat;
  }

  /**
   * `b` mapped and kept strictly after `after`: when its exact or rounded beat is not after `after`, it is placed one
   * 1/1920 step after `after` and counted in `nudged` (only there, even when it was also rounded).
   */
  mapAfter(b: Beat, after: Beat | null): Beat {
    const r = this.convert(b);
    if (after !== null && !Beats.lt(after, r.beat)) {
      this.nudged++;
      return nudgeAfter(after);
    }
    if (r.snapped) this.snapped++;
    return r.beat;
  }
}

/** Counts of a document re-timing. */
export interface RetimeCounts {
  /** Records (or BPM points) whose beat changed. */
  readonly retimed: number;
  /** Values rounded to 1/1920 beat (BeatRetimer.snapped). */
  readonly snapped: number;
  /** Values nudged one 1/1920 step after their predecessor (BeatRetimer.nudged). */
  readonly nudged: number;
}

/**
 * Re-times every beat-bearing record except BPM points (notes, keys of every track, markers, effects, videos,
 * meter marks) through `rt`. Order-preserving: keys of a track stay strictly increasing, holds keep end > t (a
 * zero-length imported hold stays zero-length), effects keep end > start, meter marks stay ordered. Fails the
 * transaction when a beat leaves the RPE range.
 */
export function retimeDocument(tx: Tx, rt: BeatRetimer): RetimeCounts {
  let retimed = 0;
  // Notes.
  for (const n of [...tx.doc.notes.values()]) {
    const t = rt.map(n.t);
    // A hold ends after it starts (an imported zero-length hold stays zero-length); other notes end where they start.
    const end = n.kind === 'hold' && !Beats.eq(n.t, n.end) ? rt.mapAfter(n.end, t) : t;
    const patch: Patch<Note> = {};
    if (!Beats.eq(t, n.t)) patch.t = t;
    if (!Beats.eq(end, n.end)) patch.end = end;
    if (Object.keys(patch).length > 0) {
      tx.update('notes', n.id, patch);
      retimed++;
    }
  }
  // Keys, per track, in order.
  for (const track of [...tx.doc.tracks.values()]) {
    const ids: readonly KeyId[] = [...tx.idx.keysByTrack(track.id)];
    const updates: Array<readonly [KeyId, Beat]> = [];
    let prev: Beat | null = null;
    for (const id of ids) {
      const k = tx.doc.keys.get(id) as Key;
      const t = rt.mapAfter(k.t, prev);
      prev = t;
      if (!Beats.eq(t, k.t)) updates.push([id, t]);
    }
    // The new beats were computed from the old order above; the index re-sorts as each key changes.
    for (const [id, t] of updates) tx.update('keys', id, { t });
    retimed += updates.length;
    fixImportedLeadIn(tx, track.id);
  }
  // Markers, effects, videos.
  for (const m of [...tx.doc.markers.values()]) {
    const t = rt.map(m.t);
    if (!Beats.eq(t, m.t)) {
      tx.update('markers', m.id, { t });
      retimed++;
    }
  }
  for (const e of [...tx.doc.effects.values()]) {
    const start = rt.map(e.start);
    const end = rt.mapAfter(e.end, start);
    if (!Beats.eq(start, e.start) || !Beats.eq(end, e.end)) {
      tx.update('effects', e.id, { start, end });
      retimed++;
    }
  }
  for (const v of [...tx.doc.videos.values()]) {
    const time = rt.map(v.time);
    if (!Beats.eq(time, v.time)) {
      tx.update('videos', v.id, { time });
      retimed++;
    }
  }
  // Meter marks (bar lines follow the music).
  const meter = tx.doc.timing.meter;
  const next: MeterMark[] = [];
  let changed = 0;
  let prev: Beat | null = null;
  for (const m of meter) {
    const t = rt.mapAfter(m.t, prev);
    prev = t;
    if (!Beats.eq(t, m.t)) changed++;
    next.push({ t, beatsPerBar: m.beatsPerBar });
  }
  if (changed > 0) {
    tx.updateSingleton('timing', { meter: next });
    retimed += changed;
  }
  if (rt.failed) tx.fail({ key: 'cmd.tempo.outOfRange' });
  return { retimed, snapped: rt.snapped, nudged: rt.nudged };
}

/** Clears `importedLeadIn` once the first key is no longer after beat 0 (snapping can move it onto beat 0). */
function fixImportedLeadIn(tx: Tx, track: TrackId): void {
  const rec = tx.doc.tracks.get(track);
  if (rec?.importedLeadIn !== true) return;
  const first = tx.idx.keysByTrack(track)[0];
  const k = first === undefined ? undefined : tx.doc.keys.get(first);
  if (k !== undefined && Beats.cmp(k.t, Beats.ZERO) <= 0) tx.update('tracks', track, { importedLeadIn: undefined });
}
